import { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import Button from './Button';

/**
 * Modal Component
 * Accessible dialog modal with backdrop
 * Features:
 * - Focus trap
 * - ESC to close
 * - Click outside to close
 * - ARIA attributes
 * - Smooth animations
 */

export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showClose = true,
  footer,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  loading = false,
}) {
  const cancelButtonRef = useRef(null);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl',
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full ${sizeClasses[size]} transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-soft-2xl transition-all`}
              >
                {/* Header */}
                {(title || showClose) && (
                  <div className="flex items-start justify-between p-6 border-b border-slate-200">
                    <div className="flex-1">
                      {title && (
                        <Dialog.Title
                          as="h3"
                          className="text-xl font-semibold text-slate-900"
                        >
                          {title}
                        </Dialog.Title>
                      )}
                      {description && (
                        <Dialog.Description className="mt-1 text-sm text-slate-600">
                          {description}
                        </Dialog.Description>
                      )}
                    </div>
                    {showClose && (
                      <button
                        type="button"
                        className="ml-4 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        onClick={onClose}
                        aria-label="Close modal"
                      >
                        <X className="w-5 h-5 text-slate-500" />
                      </button>
                    )}
                  </div>
                )}

                {/* Body */}
                <div className="p-6">
                  {children}
                </div>

                {/* Footer */}
                {(footer || onConfirm || onCancel) && (
                  <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
                    {footer || (
                      <>
                        {onCancel && (
                          <Button
                            ref={cancelButtonRef}
                            variant="outline"
                            onClick={onCancel}
                            disabled={loading}
                          >
                            {cancelText}
                          </Button>
                        )}
                        {onConfirm && (
                          <Button
                            variant={confirmVariant}
                            onClick={onConfirm}
                            loading={loading}
                          >
                            {confirmText}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

/**
 * ConfirmDialog Component
 * Pre-configured modal for confirmations
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      onConfirm={onConfirm}
      onCancel={onClose}
      confirmText={confirmText}
      cancelText={cancelText}
      confirmVariant={variant}
      loading={loading}
    >
      <p className="text-sm text-slate-700">{message}</p>
    </Modal>
  );
}
